import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { useArtists } from '../hooks/useFirestore';
import { Modal } from './Tasks';
import { Plus, Edit3, Trash2, ChevronRight, Check, X, FileText, ArrowLeft, User, ExternalLink, Tag } from 'lucide-react';
import MoodBoard from '../components/MoodBoard';

const storage = getStorage();

// ── Firestore ────────────────────────────────────────────────
function useProtocols() {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const { profile, isManager } = useAuth();

  useEffect(() => {
    const q = query(collection(db, 'protocols'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      let all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (!isManager) all = all.filter(p => p.createdBy === profile?.email || p.isGlobal || p.artistId === profile?.artistId);
      setProtocols(all);
      setLoading(false);
    });
  }, [isManager, profile]);

  const addProtocol = (data) => addDoc(collection(db, 'protocols'), { ...data, createdAt: serverTimestamp() });
  const updateProtocol = (id, data) => updateDoc(doc(db, 'protocols', id), { ...data, updatedAt: serverTimestamp() });
  const deleteProtocol = (id) => deleteDoc(doc(db, 'protocols', id));
  return { protocols, loading, addProtocol, updateProtocol, deleteProtocol };
}

const RECUR_OPTIONS = ['daily', 'weekly', 'biweekly', 'monthly'];
const PRIORITY_OPTIONS = ['P0', 'P1', 'P2'];
const CATEGORIES = ['Admin', 'Bass CreatuRa', 'Video Editing', 'Vocal'];
const CATEGORY_COLORS = {
  Admin: '#c98a18', 'Bass CreatuRa': '#1a9080', 'Video Editing': '#4a9eed', Vocal: '#a87ee8'
};

// ── Link pill ─────────────────────────────────────────────────
function LinkPill({ link }) {
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: 'rgba(26,144,128,.12)', border: '1px solid rgba(26,144,128,.25)', color: 'var(--teal2)', fontSize: 11, fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--mono)' }}>
      <ExternalLink size={10} />{link.label || 'Link'}
    </a>
  );
}

// ── Links editor ──────────────────────────────────────────────
function LinksEditor({ links, onChange }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const add = () => {
    if (!url.trim()) return;
    onChange([...links, { id: Date.now().toString(), url: url.trim(), label: label.trim() || url.trim() }]);
    setUrl(''); setLabel('');
  };
  return (
    <div className="form-group">
      <label>Links</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: links.length ? 10 : 0 }}>
        {links.map(link => (
          <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <LinkPill link={link} />
            <button onClick={() => onChange(links.filter(l => l.id !== link.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 2 }}><X size={10} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
        <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="https://..." />
        <input value={label} onChange={e => setLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="Display name" />
        <button onClick={add} disabled={!url.trim()} className="btn btn-ghost btn-sm"><Plus size={12} /> Add</button>
      </div>
    </div>
  );
}

// ── Tags editor ───────────────────────────────────────────────
function TagsEditor({ tags, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const t = input.trim().toLowerCase();
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setInput('');
  };
  return (
    <div className="form-group">
      <label>Tags</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: tags.length ? 8 : 0 }}>
        {tags.map(tag => (
          <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'var(--surface3)', color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
            {tag}
            <button onClick={() => onChange(tags.filter(t => t !== tag))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 0 }}><X size={9} /></button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="Add tag (e.g. bass, Source: Imanu)..." />
        <button onClick={add} disabled={!input.trim()} className="btn btn-ghost btn-sm">Add</button>
      </div>
    </div>
  );
}


// ── Assignee selector ─────────────────────────────────────────
function AssigneeSelect({ value, onChange, artists, label = 'Assign to', compact }) {
  const options = [
    { id: '', name: compact ? '+ assign' : 'Unassigned' },
    { id: 'avidx', name: 'Avidx' },
    { id: 'ahzira', name: 'AhziRa' },
    { id: 'jay', name: 'Jay' },
  ];
  if (compact) {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)} onClick={e => e.stopPropagation()}
        style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: value ? 'rgba(168,110,255,.15)' : 'var(--surface3)', border: `1px solid ${value ? 'var(--border-active)' : 'var(--border)'}`, color: value ? 'var(--purple2)' : 'var(--dim)', cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: 600 }}>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    );
  }
  return (
    <div className="form-group">
      <label>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}

// ── Protocol card ─────────────────────────────────────────────
function ProtocolCard({ protocol, onOpen, onDelete, canEdit }) {
  const stepCount = (protocol.steps || []).length;
  const imageCount = (protocol.images || []).length;
  const catColor = CATEGORY_COLORS[protocol.category] || 'var(--muted)';
  const sourceTags = (protocol.tags || []).filter(t => t.startsWith('source:'));

  return (
    <div onClick={() => onOpen(protocol)}
      style={{ padding: '14px 18px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'border-color .16s', display: 'flex', alignItems: 'center', gap: 14 }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-active)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>

      <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: catColor, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{protocol.title}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {protocol.category && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 20, background: `${catColor}22`, color: catColor, fontFamily: 'var(--mono)', fontWeight: 700, letterSpacing: .5 }}>
              {protocol.category.toUpperCase()}
            </span>
          )}
          {stepCount > 0 && (
            <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{stepCount} steps</span>
          )}
          {imageCount > 0 && (
            <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>🖼 {imageCount}</span>
          )}
          {sourceTags.map(t => (
            <span key={t} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: 'rgba(201,138,24,.15)', color: 'var(--amber)', fontFamily: 'var(--mono)', fontWeight: 600 }}>
              {t.replace('source:', 'Source:').replace(/^\w/, c => c.toUpperCase())}
            </span>
          ))}
          {protocol.assignedTo && (
            <span style={{ fontSize: 10, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>→ {protocol.assignedTo}</span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {canEdit && (
          <button onClick={e => { e.stopPropagation(); onDelete(protocol.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dim)', padding: 4, borderRadius: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = '#e74c3c'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
            <Trash2 size={13} />
          </button>
        )}
        <ChevronRight size={15} style={{ color: 'var(--dim)' }} />
      </div>
    </div>
  );
}

// ── Add to Tasks modal ────────────────────────────────────────
function AddToTasksModal({ protocol, artists, onClose, onAdd }) {
  const { profile } = useAuth();
  const [form, setForm] = useState({ artistId: protocol.artistId || '', assignedTo: protocol.assignedTo || '', priority: 'P1', dueDate: '', recurring: '' });
  const [done, setDone] = useState(false);
  const handleAdd = async () => {
    await onAdd(protocol, form, profile);
    setDone(true);
    setTimeout(onClose, 1200);
  };
  return (
    <Modal onClose={onClose} title={`Add to Tasks — ${protocol.title}`}>
      {done ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--teal2)', fontSize: 14, fontWeight: 600 }}>✓ Added to task board</div>
      ) : (
        <>
          <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, background: 'rgba(123,79,196,.08)', border: '1px solid rgba(123,79,196,.2)', fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
            Creates task <strong style={{ color: 'var(--text)' }}>{protocol.title}</strong>{protocol.steps?.length > 0 ? ` with ${protocol.steps.length} steps` : ''}.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Artist</label>
              <select value={form.artistId} onChange={e => setForm(f => ({ ...f, artistId: e.target.value }))}>
                <option value="">General</option>
                {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <AssigneeSelect value={form.assignedTo} onChange={v => setForm(f => ({ ...f, assignedTo: v }))} artists={artists} label="Assign to" />
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {PRIORITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Recurring</label>
              <select value={form.recurring} onChange={e => setForm(f => ({ ...f, recurring: e.target.value }))}>
                <option value="">One-time</option>
                {RECUR_OPTIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button onClick={handleAdd} className="btn btn-primary"><Plus size={14} /> Add to Tasks</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Protocol detail ───────────────────────────────────────────
function ProtocolDetail({ protocol, onBack, onSave, onDelete, onAddToTasks, canEdit, artists }) {
  const [editing, setEditing] = useState(protocol._isNew || false);

  // Migrate legacy steps into canvas items on first load
  const migrateImages = () => {
    const existing = protocol.images || [];
    const steps = protocol.steps || [];
    const hasStepItems = existing.some(i => i.type === 'step');
    if (!steps.length || hasStepItems) return existing;
    const stepItems = steps.map((s, idx) => ({
      id: `migrated_step_${s.id || idx}`,
      type: 'step',
      x: 20,
      y: 20 + idx * 100,
      w: 340,
      h: 80,
      text: s.text || '',
      stepNum: idx + 1,
      colorIdx: 4,
    }));
    return [...existing, ...stepItems];
  };

  const [form, setForm] = useState({
    title: protocol.title || '',
    category: protocol.category || 'Admin',
    notes: protocol.notes || '',
    steps: protocol.steps || [],
    links: protocol.links || [],
    images: migrateImages(),
    tags: protocol.tags || [],
    artistId: protocol.artistId || '',
    assignedTo: protocol.assignedTo || '',
    isGlobal: protocol.isGlobal || false,
  });
  const [newStep, setNewStep] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
    setEditing(false);
  };

  const addStep = () => {
    if (!newStep.trim()) return;
    setForm(f => ({ ...f, steps: [...f.steps, { id: Date.now().toString(), text: newStep.trim(), assignedTo: '' }] }));
    setNewStep('');
  };

  const catColor = CATEGORY_COLORS[form.category] || 'var(--muted)';

  return (
    <div style={{ padding: '28px 30px', position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, borderRadius: 6 }}>
          <ArrowLeft size={15} /> Protocols
        </button>
        <span style={{ color: 'var(--dim)' }}>/</span>
        <span style={{ fontSize: 13, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{form.title || 'New Protocol'}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
          {!editing && canEdit && <>
            <button onClick={() => setShowTaskModal(true)} className="btn btn-ghost btn-sm"><Plus size={13} /> Add to Tasks</button>
            <button onClick={() => setEditing(true)} className="btn btn-ghost btn-sm"><Edit3 size={13} /> Edit</button>
            <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-sm" style={{ background: 'rgba(192,57,43,.1)', color: '#e74c3c', border: '1px solid rgba(192,57,43,.3)' }}><Trash2 size={13} /></button>
          </>}
          {editing && <>
            <button onClick={() => { setEditing(false); if (protocol._isNew) onBack(); }} className="btn btn-ghost btn-sm"><X size={13} /> Cancel</button>
            <button onClick={handleSave} disabled={!form.title.trim() || saving} className="btn btn-primary btn-sm"><Check size={13} /> {saving ? 'Saving...' : 'Save'}</button>
          </>}
        </div>
      </div>

      {/* Category bar */}
      {!editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {form.category && (
            <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, background: `${catColor}22`, color: catColor, fontFamily: 'var(--mono)', fontWeight: 700, letterSpacing: .5 }}>
              {form.category.toUpperCase()}
            </span>
          )}
          {form.tags?.map(tag => (
            <span key={tag} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: tag.startsWith('source:') ? 'rgba(201,138,24,.15)' : 'var(--surface3)', color: tag.startsWith('source:') ? 'var(--amber)' : 'var(--dim)', fontFamily: 'var(--mono)' }}>
              {tag}
            </span>
          ))}
          {form.assignedTo && (
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(168,110,255,.15)', color: 'var(--purple2)', fontFamily: 'var(--mono)', fontWeight: 600 }}>
              <User size={9} style={{ display: 'inline', marginRight: 3 }} />→ {form.assignedTo}
            </span>
          )}
        </div>
      )}

      {/* Title */}
      {editing
        ? <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Protocol title..." autoFocus
            style={{ fontSize: 22, fontWeight: 800, background: 'transparent', border: 'none', borderBottom: '2px solid var(--border-active)', borderRadius: 0, padding: '4px 0', marginBottom: 20, width: '100%', outline: 'none', color: 'var(--text)' }} />
        : <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>{form.title}</h1>
      }

      {/* Edit fields */}
      {editing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
          <div className="form-group">
            <label>Category</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Artist</label>
            <select value={form.artistId} onChange={e => setForm(f => ({ ...f, artistId: e.target.value }))}>
              <option value="">General / All</option>
              {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <AssigneeSelect value={form.assignedTo} onChange={v => setForm(f => ({ ...f, assignedTo: v }))} artists={artists} label="Assign whole protocol to" />
          <div className="form-group">
            <label>Visibility</label>
            <select value={form.isGlobal ? 'global' : 'private'} onChange={e => setForm(f => ({ ...f, isGlobal: e.target.value === 'global' }))}>
              <option value="global">All users</option>
              <option value="private">Artist only</option>
            </select>
          </div>
        </div>
      )}

      {editing && <TagsEditor tags={form.tags} onChange={tags => setForm(f => ({ ...f, tags }))} />}

      {/* Links */}
      <div style={{ marginBottom: 28 }}>
        {editing
          ? <LinksEditor links={form.links} onChange={links => setForm(f => ({ ...f, links }))} />
          : form.links?.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'var(--mono)', marginBottom: 10 }}>Links</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{form.links.map(link => <LinkPill key={link.id} link={link} />)}</div>
              </>
            )
        }
      </div>

      {/* Mood board */}
      <MoodBoard items={form.images} onChange={images => setForm(f => ({ ...f, images }))} readOnly={false} showSteps notes={form.notes} links={form.links} minHeight={600} />

      {showTaskModal && <AddToTasksModal protocol={{ ...protocol, ...form }} artists={artists} onClose={() => setShowTaskModal(false)} onAdd={onAddToTasks} />}

      {showDeleteConfirm && (
        <Modal onClose={() => setShowDeleteConfirm(false)} title="Delete Protocol">
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Are you sure you want to delete <strong style={{ color: 'var(--text)' }}>{form.title}</strong>? This cannot be undone.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => { onDelete(protocol.id); onBack(); }} style={{ background: 'rgba(192,57,43,.15)', color: '#e74c3c', border: '1px solid rgba(192,57,43,.3)', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <Trash2 size={13} style={{ display: 'inline', marginRight: 5 }} /> Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function Protocols() {
  const { profile, isManager } = useAuth();
  const { artists } = useArtists();
  const { protocols, loading, addProtocol, updateProtocol, deleteProtocol } = useProtocols();
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState(null);

  const filtered = protocols.filter(p => {
    const matchCat = !catFilter || p.category === catFilter;
    const matchSearch = !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.notes?.toLowerCase().includes(search.toLowerCase()) || p.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()));
    return matchCat && matchSearch;
  });

  // Group by category
  const grouped = CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(p => p.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});
  const uncategorized = filtered.filter(p => !p.category || !CATEGORIES.includes(p.category));

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    const ref = await addProtocol({ title: newTitle.trim(), notes: '', steps: [], links: [], images: [], tags: [], assignedTo: '', category: 'Admin', artistId: isManager ? '' : (profile?.artistId || ''), isGlobal: isManager, createdBy: profile?.email });
    setShowNew(false);
    setNewTitle('');
    setSelected({ id: ref.id, title: newTitle.trim(), notes: '', steps: [], links: [], images: [], tags: [], assignedTo: '', _isNew: true });
  };

  const handleSave = async (form) => {
    if (selected?.id) { await updateProtocol(selected.id, form); setSelected(p => ({ ...p, ...form, _isNew: false })); }
  };

  const handleDelete = async (id) => { await deleteProtocol(id); };

  const handleAddToTasks = async (protocol, options, prof) => {
    await addDoc(collection(db, 'tasks'), { title: protocol.title, notes: protocol.notes || '', protocolId: protocol.id, protocolSteps: protocol.steps || [], artistId: options.artistId || '', assignedTo: options.assignedTo || '', priority: options.priority || 'P1', status: 'todo', category: 'admin', recurring: options.recurring || null, dueDate: options.dueDate || '', createdBy: prof?.email, createdAt: serverTimestamp() });
  };

  const canEdit = (p) => isManager || p.createdBy === profile?.email;

  if (loading) return <div style={{ padding: 32, color: 'var(--muted)' }}>Loading...</div>;

  if (selected) {
    return <ProtocolDetail protocol={selected} onBack={() => setSelected(null)} onSave={handleSave} onDelete={handleDelete} onAddToTasks={handleAddToTasks} canEdit={canEdit(selected)} artists={artists} />;
  }

  return (
    <div style={{ padding: '28px 30px', position: 'relative', zIndex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>📋 Protocols</h1>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{protocols.length} protocols · reusable playbooks</div>
        </div>
        <button onClick={() => setShowNew(true)} className="btn btn-primary"><Plus size={15} /> New Protocol</button>
      </div>

      {/* Search + category filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search protocols..." style={{ flex: 1, minWidth: 200 }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setCatFilter(catFilter === cat ? null : cat)}
              className={`btn btn-sm ${catFilter === cat ? 'btn-primary' : 'btn-ghost'}`}
              style={catFilter === cat ? {} : { borderColor: `${CATEGORY_COLORS[cat]}44`, color: CATEGORY_COLORS[cat] }}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Protocol list */}
      {!catFilter ? (
        <>
          {uncategorized.length === 0 && !search && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              Select a category above to browse protocols
            </div>
          )}
          {uncategorized.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--dim)', letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'var(--mono)', marginBottom: 12 }}>OTHER</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {uncategorized.map(p => <ProtocolCard key={p.id} protocol={p} onOpen={setSelected} onDelete={handleDelete} canEdit={canEdit(p)} />)}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--dim)', fontFamily: 'var(--mono)', fontSize: 12 }}>
              {search ? 'No protocols match your search' : 'No protocols in this category yet'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(p => <ProtocolCard key={p.id} protocol={p} onOpen={setSelected} onDelete={handleDelete} canEdit={canEdit(p)} />)}
          </div>
        </>
      )}

      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="New Protocol">
          <div className="form-group">
            <label>Protocol Name</label>
            <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()} placeholder="e.g. Ear Candy Session, SERUM Reese Bass..." />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowNew(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={handleCreate} disabled={!newTitle.trim()} className="btn btn-primary">Create Protocol</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export { Protocols as VocalProtocols, Protocols as AdminProtocols };
